import type { History, Location } from 'history'
import { isMobileOnly } from 'react-device-detect'
import { matchPath } from 'react-router-dom'

import { RouteName } from '@/router'
import navigationUtils from '@/utils/navigationUtils'

/**
 * - `phone`   휴대폰 전용 화면. index.html 의 기본 메타(device-width)를 그대로 쓴다.
 * - `desktop` 1024 레이아웃. 1280 보다 좁은 화면은 레이아웃째 폭에 맞춰 축소한다.
 */
export type ViewportMode = 'phone' | 'desktop'

export const isPhoneDevice = isMobileOnly

/** 휴대폰 전용 화면은 모두 /mobile 아래에 둔다 */
export const isMobilePath = (pathname: string) =>
  !!matchPath(pathname, { path: RouteName.mobileHome })

/** 학생 웹·앱·로그아웃이 여는 외부 계약 주소다. 휴대폰에서는 같은 일을 하는 /mobile 화면으로 연다 */
const PHONE_ALIASES: Partial<Record<string, string>> = {
  [RouteName.login]: RouteName.mobileLogin,
  [RouteName.qrScoring]: RouteName.mobileQr,
}

export const toPhonePath = (pathname: string) =>
  PHONE_ALIASES[pathname.replace(/(.)\/+$/, '$1')] ?? pathname

export const resolveViewportMode = ({
  isPhone,
  pathname,
}: {
  isPhone: boolean
  pathname: string
}): ViewportMode => (isPhone && isMobilePath(toPhonePath(pathname)) ? 'phone' : 'desktop')

export const desktopViewportContent = (innerWidth: number) => {
  if (innerWidth >= 1280) return null
  const userScalable = innerWidth >= 1024 ? ', user-scalable=no' : ''
  return `width=1024, initial-scale=${innerWidth / 1280}, shrink-to-fit=no${userScalable}`
}

const hrefOf = ({ pathname, search, hash }: Pick<Location, 'pathname' | 'search' | 'hash'>) =>
  `${pathname}${search}${hash}`

/**
 * 뷰포트는 문서를 불러올 때 한 번만 정한다. 불러온 뒤 메타를 바꾸면 iOS Safari 가 폭·배율을 제대로
 * 다시 잡지 않아, 휴대폰 화면과 데스크탑 화면을 오가면 문서를 새로 연다. /mobile 안의 이동은 그대로다.
 */
class ViewportService {
  mode: ViewportMode = 'desktop'

  init(history: History) {
    const { location } = history
    if (isPhoneDevice) {
      const phonePath = toPhonePath(location.pathname)
      if (phonePath !== location.pathname) history.replace({ ...location, pathname: phonePath })
    }
    this.mode = resolveViewportMode({ isPhone: isPhoneDevice, pathname: history.location.pathname })
    this.applyMeta()

    // 이동은 이미 기록에 쌓였으니 그 자리를 새 문서로 바꾼다
    history.listen((next) => this.reopenIfCrossed(next))
    // 뒤로 가기가 막히면(모달 닫기 등) history 는 이전 문서의 기록을 되돌리지 못해 주소만 바뀐 채 남는다
    window.addEventListener('popstate', () => {
      setTimeout(() => this.reopenIfCrossed(window.location))
    })
  }

  private reopenIfCrossed(next: Pick<Location, 'pathname' | 'search' | 'hash'>) {
    if (navigationUtils.isLeavingDocument) return
    if (resolveViewportMode({ isPhone: isPhoneDevice, pathname: next.pathname }) === this.mode) {
      return
    }
    const pathname = isPhoneDevice ? toPhonePath(next.pathname) : next.pathname
    navigationUtils.replaceDocument(hrefOf({ ...next, pathname }))
  }

  private applyMeta() {
    if (this.mode === 'phone') return
    const content = desktopViewportContent(window.innerWidth)
    if (!content) return
    const meta = document.createElement('meta')
    meta.name = 'viewport'
    meta.content = content
    document.head.appendChild(meta)
  }
}

export const viewportService = new ViewportService()
